/**
 * Theme constants and configuration
 */

window.theme = window.theme || {};

// Theme configuration
window.theme.config = {
  // Breakpoints (matches Tailwind defaults)
  breakpoints: {
    sm: 640,
    md: 768,
    lg: 1024,
    xl: 1280,
    '2xl': 1536
  },
  
  // Animation durations
  animations: {
    fast: 150,
    normal: 300,
    slow: 500
  },
  
  // Money formatting
  moneyFormat: '${{amount}}',
  moneyWithCurrencyFormat: '${{amount}} USD',
  
  // Cart settings
  cart: {
    type: 'drawer', // 'drawer', 'page', or 'ajax'
    showShippingCalculator: true,
    showNoteField: true
  },
  
  // Product settings
  product: {
    imageZoomEnabled: true,
    variantImageChangeEnabled: true,
    showVendor: false,
    showSKU: false,
    showInventoryQuantity: false,
    lowInventoryThreshold: 10
  },
  
  // Collection settings
  collection: {
    productsPerPage: 24,
    enableFiltering: true,
    enableSorting: true,
    defaultSort: 'manual'
  },
  
  // Search settings
  search: {
    enabled: true,
    showVendor: false,
    showPrice: true,
    resultsPerPage: 10
  },
  
  // Social media
  social: {
    platforms: ['facebook', 'instagram', 'twitter', 'pinterest', 'youtube'],
    shareEnabled: true
  },
  
  // Accessibility
  a11y: {
    skipLinkTarget: 'MainContent',
    focusableElements: 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    announcementDelay: 500
  }
};

// Theme strings for internationalization
window.theme.strings = {
  // General
  loading: 'Loading...',
  error: 'Something went wrong. Please try again.',
  success: 'Success!',
  
  // Product
  addToCart: 'Add to cart',
  soldOut: 'Sold out',
  unavailable: 'Unavailable',
  unitPrice: 'Unit price',
  unitPriceSeparator: 'per',
  onlyXLeft: 'Only {{count}} left!',
  
  // Cart
  cartEmpty: 'Your cart is empty',
  cartError: 'There was an error updating your cart',
  cartAdded: 'Added to cart!',
  cartUpdated: 'Cart updated',
  cartRemoved: 'Item removed from cart',
  
  // Search
  searchPlaceholder: 'Search products...',
  searchNoResults: 'No results found for "{{terms}}"',
  searchResults: '{{count}} results for "{{terms}}"',
  
  // Forms
  required: 'This field is required',
  invalidEmail: 'Please enter a valid email address',
  newsletterSuccess: 'Thanks for subscribing!',
  contactSuccess: 'Thank you for your message. We\'ll get back to you soon!',
  
  // Accessibility
  skipToContent: 'Skip to content',
  closeDialog: 'Close dialog',
  previousImage: 'Previous image',
  nextImage: 'Next image',
  imageOf: 'Image of {{product}}',
  openCart: 'Open cart',
  openMenu: 'Open menu',
  closeMenu: 'Close menu'
};

// Shopify-specific constants
window.theme.shopify = {
  routes: {
    root: '/',
    cart: '/cart',
    cartAdd: '/cart/add.js',
    cartChange: '/cart/change.js',
    cartUpdate: '/cart/update.js',
    cartClear: '/cart/clear.js',
    search: '/search',
    searchSuggest: '/search/suggest.json',
    predictiveSearch: '/search/suggest'
  },
  
  // Liquid variables that will be populated by the theme
  liquid: {
    shopName: '',
    shopUrl: '',
    cartItemCount: 0,
    customerLoggedIn: false,
    templateName: '',
    locale: 'en'
  }
};

// Utility functions
window.theme.utils = {
  // Get current breakpoint
  getCurrentBreakpoint: function() {
    const width = window.innerWidth;
    const breakpoints = window.theme.config.breakpoints;
    
    if (width >= breakpoints['2xl']) return '2xl';
    if (width >= breakpoints.xl) return 'xl';
    if (width >= breakpoints.lg) return 'lg';
    if (width >= breakpoints.md) return 'md';
    if (width >= breakpoints.sm) return 'sm';
    return 'xs';
  },
  
  // Check if mobile
  isMobile: function() {
    return window.innerWidth < window.theme.config.breakpoints.md;
  },
  
  // Check if desktop
  isDesktop: function() {
    return window.innerWidth >= window.theme.config.breakpoints.lg;
  },
  
  // Format money
  formatMoney: function(cents, format) {
    format = format || window.theme.config.moneyFormat;
    return window.formatMoney(cents, format);
  },
  
  // Get product handle from URL
  getProductHandle: function() {
    const pathSegments = window.location.pathname.split('/');
    return pathSegments[pathSegments.length - 1];
  },
  
  // Update URL parameters
  updateUrlParameter: function(key, value) {
    const url = new URL(window.location);
    if (value) {
      url.searchParams.set(key, value);
    } else {
      url.searchParams.delete(key);
    }
    window.history.pushState({}, '', url);
  },
  
  // Scroll to element
  scrollToElement: function(element, offset = 0) {
    if (typeof element === 'string') {
      element = document.querySelector(element);
    }
    
    if (element) {
      const elementTop = element.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({
        top: elementTop - offset,
        behavior: 'smooth'
      });
    }
  },
  
  // Trap focus within element
  trapFocus: function(container, focusableElementsString) {
    focusableElementsString = focusableElementsString || window.theme.config.a11y.focusableElements;
    
    const focusableElements = container.querySelectorAll(focusableElementsString);
    const firstFocusableElement = focusableElements[0];
    const lastFocusableElement = focusableElements[focusableElements.length - 1];
    
    container.addEventListener('keydown', function(e) {
      const isTabPressed = e.key === 'Tab';
      
      if (!isTabPressed) return;
      
      if (e.shiftKey) {
        if (document.activeElement === firstFocusableElement) {
          lastFocusableElement.focus();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === lastFocusableElement) {
          firstFocusableElement.focus();
          e.preventDefault();
        }
      }
    });
    
    firstFocusableElement.focus();
  }
};

// Initialize theme
document.addEventListener('DOMContentLoaded', function() {
  // Set initial breakpoint
  document.body.setAttribute('data-breakpoint', window.theme.utils.getCurrentBreakpoint());
  
  // Update breakpoint on resize
  window.addEventListener('resize', window.debounce(function() {
    document.body.setAttribute('data-breakpoint', window.theme.utils.getCurrentBreakpoint());
  }, 250));
  
  // Add theme classes to body
  document.body.classList.add('theme-loaded');
  
  if (window.theme.utils.isMobile()) {
    document.body.classList.add('is-mobile');
  } else {
    document.body.classList.add('is-desktop');
  }
});